import { Modal, Radio, Select, Row, Col, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { mixPostV1 } from "../../../services/mixin";
import { invalidateCustomerCaches } from "../../../api/ChannelCustomer";
import { extractApiError } from "../../../lib/apiError";

/**
 * Hộp thoại cập nhật Giới tính + Ngày sinh của khách.
 *  - gender theo API /be/customers: 0 = không xác định, 1 = Nam, 2 = Nữ
 *  - birthday dạng YYYY-MM-DD ("" nghĩa là chưa có)
 * Khách đã có hồ sơ CRM (customerId) -> lưu thẳng lên API; chưa có -> chỉ trả
 * giá trị về cho form cha để gửi kèm lúc tạo khách.
 */
interface Props {
    open: boolean;
    onClose: () => void;
    gender?: number;
    birthday?: string;
    /** ID khách hàng CRM; null nghĩa là khách chưa được tạo */
    customerId?: number | null;
    displayName?: string;
    onSaved: (data: { gender: number; birthday: string }) => void;
}

export default function GenderBirthdayModal({ open, onClose, gender = 0, birthday = "", customerId, displayName, onSaved }: Props) {
    const { t, i18n } = useTranslation();
    const [genderDraft, setGenderDraft] = useState(gender);
    const [birthDraft, setBirthDraft] = useState<{ d?: number; m?: number; y?: number }>({});
    const [saving, setSaving] = useState(false);

    // Mỗi lần mở lại -> nạp giá trị hiện tại của khách
    useEffect(() => {
        if (!open) return;
        setGenderDraft(gender || 0);
        const [y, m, d] = (birthday || "").split("-");
        setBirthDraft({ d: Number(d) || undefined, m: Number(m) || undefined, y: Number(y) || undefined });
    }, [open, gender, birthday]);

    const currentYear = new Date().getFullYear();
    const dayOptions = Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1).padStart(2, "0") }));
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: String(i + 1).padStart(2, "0") }));
    const yearOptions = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => ({ value: currentYear - i, label: String(currentYear - i) }));

    const handleSave = async () => {
        const { d, m, y } = birthDraft;
        // Được phép bỏ trống ngày sinh, nhưng đã chọn thì phải đủ 3 ô và là ngày có thật
        if ((d || m || y) && !(d && m && y)) {
            message.warning(t("roomchat.birthday_incomplete") || "Vui lòng chọn đủ ngày, tháng, năm");
            return;
        }
        let nextBirthday = "";
        if (d && m && y) {
            const dt = new Date(y, m - 1, d);
            if (dt.getDate() !== d || dt.getMonth() !== m - 1) {
                message.warning(t("roomchat.birthday_invalid") || "Ngày sinh không hợp lệ");
                return;
            }
            nextBirthday = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        }

        if (customerId) {
            setSaving(true);
            const res = await mixPostV1(`/api/v1/be/customers/${customerId}`, {
                display_name: displayName,
                gender: genderDraft,
                birthday: nextBirthday || null,
            });
            invalidateCustomerCaches();
            setSaving(false);
            if (!res.status) {
                message.error(extractApiError(res, i18n.language) || t("roomchat.update_failed") || "Cập nhật thất bại");
                return;
            }
            message.success(t("roomchat.update_success") || "Cập nhật thành công");
        }
        onSaved({ gender: genderDraft, birthday: nextBirthday });
        onClose();
    };

    return (
        <Modal
            open={open}
            title={t("roomchat.gender_birthday") || "Giới tính và Ngày sinh"}
            onCancel={onClose}
            onOk={handleSave}
            okText={t("roomchat.save") || "Lưu"}
            cancelText={t("roomchat.cancel") || "Hủy"}
            confirmLoading={saving}
            width={420}
        >
            <div className="mb-2 font-medium text-000000e0">{t("roomchat.gender") || "Giới tính"}</div>
            <Radio.Group
                value={genderDraft}
                onChange={(e) => setGenderDraft(e.target.value)}
                className="mb-4"
            >
                <Radio value={1}>{t("roomchat.male") || "Nam"}</Radio>
                <Radio value={2}>{t("roomchat.female") || "Nữ"}</Radio>
                <Radio value={0}>{t("roomchat.gender_unknown") || "Không xác định"}</Radio>
            </Radio.Group>

            <div className="mb-2 font-medium text-000000e0">{t("roomchat.birthday") || "Ngày sinh"}</div>
            <Row gutter={8}>
                <Col span={8}>
                    <Select
                        className="w-full"
                        placeholder={t("roomchat.day") || "Ngày"}
                        value={birthDraft.d}
                        onChange={(val) => setBirthDraft((prev) => ({ ...prev, d: val }))}
                        options={dayOptions}
                        allowClear
                    />
                </Col>
                <Col span={8}>
                    <Select
                        className="w-full"
                        placeholder={t("roomchat.month") || "Tháng"}
                        value={birthDraft.m}
                        onChange={(val) => setBirthDraft((prev) => ({ ...prev, m: val }))}
                        options={monthOptions}
                        allowClear
                    />
                </Col>
                <Col span={8}>
                    <Select
                        className="w-full"
                        placeholder={t("roomchat.year") || "Năm"}
                        value={birthDraft.y}
                        onChange={(val) => setBirthDraft((prev) => ({ ...prev, y: val }))}
                        options={yearOptions}
                        showSearch
                        allowClear
                    />
                </Col>
            </Row>
        </Modal>
    );
}
